/**
 * Economy Chain domain — versioned Zod shapes (ECONOMY-1 phase C1).
 *
 * The vocabulary of the Economy Chain playbook as it crosses boundaries: research
 * products and intake rounds, the analyst overlay (every human decision layered over
 * the researched dataset), the §2 evidence record with lineage, the supply & demand
 * op log, and the background-sweep worker shapes. The web app's pure core keeps its
 * own working types (`services/economy-radar/src/core/types.ts`); these schemas are
 * the boundary parse (ADR 0005 — parse, don't validate) shared by both wire sides.
 */
import { z } from 'zod';

// ── Playbook vocabulary ──────────────────────────────────────────────────────

/** The four triage verdicts — the intake fork every product goes through. */
export const ChainVerdictSchema = z.enum(['interested', 'explore', 'maybelater', 'discarded']);
export type ChainVerdict = z.infer<typeof ChainVerdictSchema>;

/** Researcher note shorthand recorded in the source documents. */
export const ChainSignalSchema = z.enum(['yes', 'maybe', 'later', 'no']);
export type ChainSignal = z.infer<typeof ChainSignalSchema>;

/**
 * Where a product is actually available — a **set**, because most products serve more than one
 * market and "Global" is a different claim from "USA and the GCC".
 *
 * A closed list rather than free text (ECONOMY-21 I2): it is a fact the research can support from
 * the source — a pricing page's currencies, a storefront's country list — and a typed value is
 * filterable where a sentence is not. Empty means the research could not establish it, which is
 * different from "global" and must not be rendered as it.
 */
export const ChainMarketRegionSchema = z.enum(['Global', 'USA', 'EU', 'UK', 'KSA', 'GCC', 'MENA', 'APAC', 'LATAM', 'Africa']);
export type ChainMarketRegion = z.infer<typeof ChainMarketRegionSchema>;

/**
 * Research objective keys — the markets under research.
 *
 * A (Global & U.S. AI B2C) and B (Saudi Arabia AI B2C) are the two that run. Four more were
 * seeded and never switched on; ECONOMY-14 removed them. This enum is the single source of the
 * vocabulary — every list of markets derives from `.options` rather than restating the letters,
 * so adding the venture-capital market later is one edit here.
 */
export const ChainObjectiveKeySchema = z.enum(['A', 'B']);
export type ChainObjectiveKey = z.infer<typeof ChainObjectiveKeySchema>;

/** §5 — exactly one classification per category. */
export const ChainClassificationSchema = z.enum(['open', 'crowded', 'emerging', 'weak', 'toohard']);
export type ChainClassification = z.infer<typeof ChainClassificationSchema>;

// ── Research dataset ─────────────────────────────────────────────────────────

/**
 * A researched product — one row of an intake round (or a manual analyst entry).
 *
 * **Only what a source can state** (ECONOMY-21 I2). `tam`, `ocean`, `growth`, `funding`, `model`,
 * `target` and the free-text `industry` were dropped together: every one of them was either
 * asserted by a model with nothing behind it or read by nothing at all, and an unsourced field on
 * screen is indistinguishable from a fabricated one.
 */
/**
 * The machine-owned pipeline stage of a product (ADR 0118 §3).
 *
 * A closed set, so an unknown value is refused at the boundary rather than rendering as a fifth
 * state nothing has a label for.
 */
export const ChainProductStageSchema = z.enum(['discovered', 'researching', 'evidenced', 'retired']);
export type ChainProductStage = z.infer<typeof ChainProductStageSchema>;

export const ChainProductSchema = z.object({
  id: z.string().min(1),
  /** Stable base key within its round (dedup across rounds uses this). */
  base: z.string().min(1),
  company: z.string(),
  product: z.string().min(1),
  /**
   * The GICS industry group the research filed it under — the product's **subcategory**, and the
   * category the taxonomy groups by. Its *industry* (the GICS sector) is derived from this through
   * the taxonomy rather than stored beside it, so the two can never disagree after a re-filing.
   *
   * Empty means nothing has said, which the UI renders as "Unfiled" rather than as a guess.
   */
  subcategory: z.string().max(120).default(''),
  /**
   * What established the `subcategory` — the sentence on the product's own pages naming who it is
   * for (ADR 0120 §2). `null` means nothing did: either it is unfiled, or a person filed it by hand.
   *
   * This is not decoration. A classification the research asserted and one an analyst typed render
   * identically without it, and so does a classification the model invented — which is precisely the
   * failure ADR 0115 exists to prevent ("nothing on screen without a source"). The board already
   * holds every feature to this standard; the category was the one claim exempt from it.
   */
  market: z
    .object({
      /** Who the customer is, in the page's own words: "recruiting teams", "Shopify stores". */
      customer: z.string().max(200),
      sourceUrl: z.string().max(500),
      /** Verbatim. A paraphrase here would be the citation asserting something the page did not. */
      quote: z.string().max(1000),
    })
    .nullable()
    .default(null),
  /**
   * Why this product has no `subcategory` (ADR 0120 §2). Empty when it IS filed.
   *
   * A plain string, deliberately, where the wire event uses a closed enum. The two are different
   * questions. On the wire an unrecognised value means the producer is broken and refusing it is
   * right. Here the column is OUR OWN store and legitimately holds values no producer sends —
   * `not-recorded` for every product filed before the reason was kept — so refusing an unknown
   * value at the boundary would reject the board's own history. The UI phrases the ones it knows
   * and shows the rest as they are, which degrades into a slightly technical sentence rather than
   * a blank row.
   */
  marketRefused: z.string().max(60).default(''),
  does: z.string(),
  link: z.string(),
  /** Where the product is actually available. Empty = the research could not establish it. */
  regions: z.array(ChainMarketRegionSchema).max(10).default([]),
  /** The researcher's verbatim note. */
  note: z.string(),
  /** The researcher's initial verdict shorthand. */
  signal: ChainSignalSchema,
  reason: z.string(),
  obj: ChainObjectiveKeySchema,
  intakeId: z.string().min(1),
  round: z.string(),
  date: z.string(),
  manual: z.boolean().optional(),
  /**
   * The page an agent learned this from (ECONOMY-20) — not the product's own address, which is
   * `link`. A find's citation and a company's home page answer different questions, and the one
   * an analyst needs to judge trust is this one.
   *
   * `null` means genuinely no citation, and covers two different-looking cases the UI must
   * distinguish by other means: an agent that asserted a product without one, and every product
   * projected before this field existed — which can never be back-filled, because the value was
   * discarded at the moment it was written. Empty for a manual entry and a document extraction,
   * which have an uploader and a file instead.
   */
  sourceUrl: z.string().max(500).nullable().default(null),
  /**
   * Did the code that wrote this row record where a find came from? (N60)
   *
   * The ONLY thing that separates two opposite readings of `sourceUrl: null` — *the agent gave no
   * source*, which is a reason to distrust the row, and *this predates us keeping sources*, which
   * is a reason to distrust nothing but our own records. ECONOMY-20 guessed at it from whether any
   * sibling in the round had one; this is the fact itself.
   *
   * Defaults true because every producer writing today records them. Only rows that predate the
   * column are false, and the migration marks those explicitly rather than leaving it to inference.
   */
  sourceRecorded: z.boolean().default(true),
  /**
   * Where this product is in the research pipeline (ADR 0118) — machine-owned, and deliberately
   * NOT the analyst's `signal`.
   *
   * `discovered` → on the board, nothing asked yet. `researching` → a request is durably out.
   * `evidenced` → features were established, with sources. `retired` → the agent answered and there
   * is nothing to show, and `stageReason` says which kind of nothing.
   *
   * Before this existed, all four looked identical on screen: a product with no features. The
   * distinction between "we could not read its sources" and "we read them and they supported
   * nothing" is the one four diagnostic cycles were spent failing to make.
   */
  stage: ChainProductStageSchema.default('discovered'),
  /** Why a product is `retired`. Empty for every other stage. */
  stageReason: z.string().max(280).default(''),
});
export type ChainProduct = z.infer<typeof ChainProductSchema>;

/** One intake round — one uploaded research document per objective. */
export const ChainIntakeRoundSchema = z.object({
  id: z.string().min(1),
  obj: ChainObjectiveKeySchema,
  date: z.string(),
  label: z.string().min(1),
  doc: z.string(),
  status: z.enum(['parsed', 'pending']),
});
export type ChainIntakeRound = z.infer<typeof ChainIntakeRoundSchema>;

/**
 * One uploaded research document (ADR 0098) — the metadata for a blob in the private
 * `intake-docs` bucket. The row is the record and the bucket holds the bytes; the row
 * names their path, so a blob is never orphaned without something pointing at it.
 *
 * `status` mirrors the extraction sweep's lifecycle so this row alone answers "what
 * happened to my upload?" — `failed` always carries `error`, and `extracted` always
 * carries the count, because a document that finished with neither is indistinguishable
 * from one still in flight.
 */
export const ChainIntakeDocumentSchema = z.object({
  id: z.string().min(1),
  /** The intake round it was dropped on. */
  roundId: z.string().min(1),
  /**
   * The DISPLAY name — editable, and what every screen shows (ADR 0109). Starts as the uploaded
   * name and is the label the intake round takes.
   */
  filename: z.string().min(1),
  /**
   * The name the file actually arrived under, written once and never updated.
   *
   * Not redundant with `filename` (which is now editable) nor with `storage_path` (which folds
   * punctuation away and cannot be reversed). Without this column the first rename would destroy
   * the only verbatim record of the upload — found in review, ADR 0109 decision 2. Null for
   * documents uploaded before the column existed.
   */
  originalFilename: z.string().nullable().default(null),
  /** Who uploaded it — the resolved principal (ADR 0106). Null when nobody was identifiable. */
  uploadedBy: z.string().nullable().default(null),
  mime: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  /**
   * `review` (ADR 0100 §4) is the state that matters: rows have been staged but **no product
   * exists yet**. `extracted` is kept for documents ingested under ADR 0098, whose rows went
   * straight into the workspace; `committed` is the new terminal state, reached only when a
   * person presses Submit. Distinguishing them is the difference between "your document was
   * read" and "your document is in the pipeline".
   */
  status: z.enum(['stored', 'extracting', 'review', 'extracted', 'committed', 'failed']),
  /** The `document` sweep extracting it (null until enqueued). */
  sweepId: z.string().nullable(),
  /** Why extraction failed, in the analyst's words (null while healthy). */
  error: z.string().nullable(),
  /** Products the extraction produced (null until it finishes). */
  extracted: z.number().int().nonnegative().nullable(),
  uploadedAt: z.string(),
});
export type ChainIntakeDocument = z.infer<typeof ChainIntakeDocumentSchema>;

/**
 * The formats the upload gate accepts, and the ceiling it accepts them under.
 *
 * Shared rather than duplicated because the browser checks them to fail fast and the
 * server checks them because the browser is untrusted — two checks, one list, so the
 * message an analyst sees can never disagree with the rule that rejected them.
 */
export const INTAKE_DOCUMENT_MAX_BYTES = 20 * 1024 * 1024;
export const INTAKE_DOCUMENT_EXTENSIONS = ['.docx', '.pdf', '.md', '.markdown', '.txt', '.csv', '.tsv'] as const;

// ── Staged intake: capture → map → review → commit (ADR 0100) ────────────────

/**
 * **L2 — the document's own structure, captured verbatim.**
 *
 * Schema-less on purpose. `columns` are the document's OWN headers, unrenamed and
 * unmapped, and a cell nobody recognises is kept rather than dropped — an unrecognised
 * column is exactly the case where dropping it silently loses the analyst's work.
 *
 * This layer is deliberately produced by code, not by a model: a `.docx` table and a
 * `.csv` both have an unambiguous structure that can be read exactly, and a model asked
 * to re-type a table it can already see will occasionally paraphrase a cell. Judgement
 * happens at L3.
 */
export const IntakeCaptureSchema = z.object({
  /** `table` when the document held a real grid; `prose` when all we have is text. */
  kind: z.enum(['table', 'prose']),
  /** The document's own header row, in document order. Empty for prose. */
  columns: z.array(z.string()).default([]),
  /** One entry per row: the document's own column name → its cell, verbatim. */
  rows: z.array(z.record(z.string(), z.string())).default([]),
  /** Text outside any table, one entry per paragraph — kept so nothing is lost. */
  blocks: z.array(z.string()).default([]),
});
export type IntakeCapture = z.infer<typeof IntakeCaptureSchema>;

/**
 * The mappable half of a product — everything a document can state about one, and
 * nothing the server owns (`id`, `intakeId`, `round`, `date`, `obj` are assigned at
 * commit).
 *
 * **`null` means "the document did not say", and is never a guess.** It is distinct
 * from `''`, which would claim the document stated an empty value. `signal` is nullable
 * rather than defaulted because a default here would be an invention: it is a judgement
 * most research tables never record. Commit substitutes the neutral value (`maybe`).
 *
 * Shrunk with the product record it stages (ECONOMY-21 I2): a document column mapped onto a field
 * the board no longer holds would be read, staged, reviewed and then silently dropped at commit.
 */
export const StagedProductFieldsSchema = z.object({
  company: z.string().default(''),
  product: z.string().default(''),
  /** The GICS industry group, if the document named one. */
  subcategory: z.string().default(''),
  does: z.string().default(''),
  link: z.string().default(''),
  /** The document's own comment on this row, verbatim. */
  note: z.string().default(''),
  /** The verdict the DOCUMENT recorded, if it had a comment/verdict column. */
  signal: ChainSignalSchema.nullable().default(null),
  reason: z.string().default(''),
});
export type StagedProductFields = z.infer<typeof StagedProductFieldsSchema>;

/**
 * The analyst's edits (L4) — every field optional, and explicitly allowed to be `undefined`.
 *
 * Spelled out rather than `Partial<StagedProductFields>` because the repo runs
 * `exactOptionalPropertyTypes`, under which `Partial<T>` means "the key may be absent" but NOT
 * "the key may be present holding undefined" — and a JSON round-trip through the database
 * produces exactly the latter.
 */
export const StagedProductOverridesSchema = StagedProductFieldsSchema.partial();
export type StagedProductOverrides = z.infer<typeof StagedProductOverridesSchema>;

/**
 * **L3 + L4 — one row waiting for a person.**
 *
 * `mapped` is what the model made of the document and is never edited; `overrides` is
 * the analyst's edits, field by field. Keeping them apart is the whole point: when a row
 * looks wrong, "did the model mis-read the document or did I mis-correct it?" is
 * answerable by looking. `source` carries the document's own cells for the row, so the
 * mapping can be checked against the thing it was mapped from without re-reading the file.
 */
export const ChainStagedProductSchema = z.object({
  id: z.string().min(1),
  documentId: z.string().min(1),
  roundId: z.string().min(1),
  /** Index into the capture's `rows` — the link back to what the document said. */
  sourceRow: z.number().int().nonnegative(),
  /** The document's own cells for this row (its column names, its values). */
  source: z.record(z.string(), z.string()).default({}),
  /** L3 — the model's mapping. Immutable. */
  mapped: StagedProductFieldsSchema,
  /** L4 — the analyst's edits. Empty until they change something. */
  overrides: StagedProductOverridesSchema.default({}),
  /** Excluded rows stay visible and are not committed. */
  included: z.boolean().default(true),
  status: z.enum(['review', 'committed', 'discarded']),
});
export type ChainStagedProduct = z.infer<typeof ChainStagedProductSchema>;

/** The fields a document's column can be mapped onto. */
export const MappableFieldSchema = z.enum([
  'company', 'product', 'subcategory', 'does', 'link', 'note', 'signal', 'reason',
]);
export type MappableField = z.infer<typeof MappableFieldSchema>;

/**
 * **What the model decides at L3 — which column is which, not what the cells say.**
 *
 * The model returns a COLUMN MAPPING rather than re-typed rows, and that is the load-bearing
 * choice in this layer. Three reasons, in order of how much they matter:
 *
 * 1. **The model cannot paraphrase a cell.** Values are transferred from L2 verbatim by pure
 *    code. Asked to re-type a 30-row table, a model will eventually smooth a value — and a
 *    silently reworded business model is exactly the corruption this whole redesign exists
 *    to stop.
 * 2. **Output is O(columns), not O(rows).** A 200-row research table costs the same to map as
 *    a 3-row one, and cannot be truncated by an output-token ceiling half way down.
 * 3. **It is inspectable and re-appliable.** "Which column did you think was the verdict?" is
 *    answerable, and a corrected mapping can be re-run over the same capture for free.
 *
 * `verdicts` maps the DISTINCT values of the verdict column onto our signal — an analyst's
 * "education — not an interesting industry" means `no`, and there are only ever a handful of
 * distinct comments however many rows there are.
 */
export const ColumnMappingSchema = z.object({
  /** The document's column name → our field. `null` = recognised but deliberately unmapped. */
  columns: z.record(z.string(), MappableFieldSchema.nullable()).default({}),
  /** Distinct verdict-column values → our signal. `null` = the comment implies no verdict. */
  verdicts: z.record(z.string(), ChainSignalSchema.nullable()).default({}),
  /** Why the model read the table this way — shown in review so the mapping is auditable. */
  reading: z.string().default(''),
});
export type ColumnMapping = z.infer<typeof ColumnMappingSchema>;

/**
 * What Submit actually commits: L3 with L4 laid over it, field by field.
 *
 * Lives in contracts rather than in either side because the API writes it and the UI
 * previews it, and two implementations of "what gets committed" would eventually
 * disagree about exactly the rows the analyst was trusting.
 */
export function resolveStagedFields(row: {
  readonly mapped: StagedProductFields;
  readonly overrides: StagedProductOverrides;
}): StagedProductFields {
  const out = { ...row.mapped };
  for (const [key, value] of Object.entries(row.overrides)) {
    if (value !== undefined) Object.assign(out, { [key]: value });
  }
  return out;
}

// ── §2 evidence with lineage ─────────────────────────────────────────────────

/** Evidence provenance class — public fact, labelled estimate, or analyst judgment. */
export const ChainEvidenceTypeSchema = z.enum(['public', 'estimate', 'judgment']);
export type ChainEvidenceType = z.infer<typeof ChainEvidenceTypeSchema>;

export const ChainEvidenceConfidenceSchema = z.enum(['strong', 'medium', 'weak']);
export type ChainEvidenceConfidence = z.infer<typeof ChainEvidenceConfidenceSchema>;

/**
 * A partial override of one A–F evidence field — every data point carries its lineage
 * (§2): what it says, where it came from, when it was checked, and how much to trust it.
 * Partial because the analyst edits one property at a time over the seeded record.
 */
export const ChainEvidencePatchSchema = z
  .object({
    value: z.string(),
    sourceName: z.string(),
    sourceUrl: z.string(),
    checked: z.string(),
    type: ChainEvidenceTypeSchema,
    conf: ChainEvidenceConfidenceSchema,
  })
  .partial();
export type ChainEvidencePatch = z.infer<typeof ChainEvidencePatchSchema>;

/** One needs↔features pair (consumer demand matched to what the product supplies). */
export const ChainFeatureRowSchema = z.object({
  need: z.string(),
  feature: z.string(),
  sourceName: z.string(),
  sourceUrl: z.string(),
});
export type ChainFeatureRow = z.infer<typeof ChainFeatureRowSchema>;

/** One similar/alternative product row with its market coverage tag. */
export const ChainSimilarRowSchema = z.object({
  name: z.string().min(1),
  type: z.string(),
  url: z.string(),
  note: z.string(),
  /** Simulated seed row vs analyst-added. */
  simulated: z.boolean(),
  region: z.enum(['global', 'both', 'saudi']),
});
export type ChainSimilarRow = z.infer<typeof ChainSimilarRowSchema>;

// ── Projected evidence: what an agent run put on the board (ECONOMY-21) ──────

/**
 * A cited, fact-checked feature as the BOARD holds it.
 *
 * `quote` is stored inline beside `blockId`, not instead of it. A pointer alone means the citation
 * renders only when the warehouse is reachable, and a citation that can fail to load is worse than
 * none: the analyst cannot tell "unsourced" from "temporarily unavailable". The `blockId` rides
 * along so a claim can be re-verified against the block it came from.
 */
export const ChainProjectedFeatureSchema = z.object({
  feature: z.string().min(1).max(300),
  need: z.string().max(300).default(''),
  sourceUrl: z.string().min(1).max(500),
  sourceName: z.string().max(200).default(''),
  quote: z.string().min(1).max(1000),
  blockId: z.string().max(200).default(''),
});
export type ChainProjectedFeature = z.infer<typeof ChainProjectedFeatureSchema>;

/**
 * One real public comment about a product — verbatim, with a real author and a real permalink.
 *
 * `kind` is the honesty valve. A thread found by searching a product's NAME may be about something
 * else entirely, so it is a **mention** unless the product's own domain appears in it, in which
 * case it is a **review**. `matchedBy` names the query that found it, so the distinction is
 * auditable rather than asserted.
 */
export const ChainOpinionSchema = z.object({
  source: z.enum(['producthunt', 'reddit']),
  kind: z.enum(['review', 'mention']),
  externalId: z.string().min(1).max(200),
  body: z.string().min(1).max(4000),
  authorName: z.string().max(200).default(''),
  authorUrl: z.string().max(500).default(''),
  permalink: z.string().min(1).max(500),
  postedAt: z.string().max(64).default(''),
  score: z.number().int().nullable().default(null),
  matchedBy: z.string().max(300).default(''),
});
export type ChainOpinion = z.infer<typeof ChainOpinionSchema>;

/**
 * Everything a research run established about one product beyond the row itself.
 *
 * Kept apart from the analyst's `features`/`similar` overlay on purpose: a re-run must be able to
 * refresh what it found without touching what a person wrote. The app reads the analyst's rows
 * when they exist and these when they do not — the agent never overwrites a human.
 */
export const MAX_PROJECTED_FEATURES = 60;
export const MAX_PROJECTED_OPINIONS = 200;

/**
 * A list that drops the items it cannot read, rather than refusing the list.
 *
 * Deliberately not `z.array(ItemSchema)`, and the reason is the blast radius. This schema is
 * parsed in two places that both hold the whole workspace: the API `.parse()`s the chain response
 * it is about to send, and the app `.safeParse()`s the one it receives. Under a strict array, one
 * evidence row with an empty quote — a row written before a rule tightened, a truncated write —
 * would 500 the endpoint and blank every screen for that owner. A workspace of eight products
 * must not be lost to one bad feature on one of them.
 *
 * The strictness itself is kept where it belongs: an item that fails the check still never
 * reaches the screen. It is dropped alone.
 */
function lenientList<Item extends z.ZodTypeAny>(item: Item, cap: number) {
  return z
    .array(z.unknown())
    .default([])
    .transform((rows) =>
      rows.slice(0, cap).flatMap((row): z.infer<Item>[] => {
        const parsed = item.safeParse(row);
        return parsed.success ? [parsed.data as z.infer<Item>] : [];
      }),
    );
}

/**
 * One demand entry as the BOARD reads it back (ADR 0128).
 *
 * Deliberately looser than the wire schema, and for the reason `lenientList` exists: this parses
 * rows already in the database, where a future producer may have written a shape this build does
 * not know. A demand entry that fails to parse is dropped from the list rather than taking the
 * whole board down — but the fields the screen depends on to tell a reader WHAT they are looking at
 * (`derivation`, and the `quote`/`fromFeature` that back it) are required, because an entry that
 * cannot say how it was derived must not render at all.
 */
export const ChainDemandEntrySchema = z.object({
  derivation: z.enum(['vendor-stated', 'inferred-from-feature']),
  need: z.string().min(1).max(300),
  kind: z.enum(['pain', 'need', 'want']).default('need'),
  sourceUrl: z.string().min(1).max(2000),
  sourceName: z.string().max(200).default(''),
  /** Present on `vendor-stated` only — the sentence the page actually carries. */
  quote: z.string().max(1000).optional(),
  /** Present on `inferred-from-feature` only — the kept feature the need was read off. */
  fromFeature: z.string().max(200).optional(),
  signals: z
    .array(
      z.object({
        kind: z.enum(['supply-exists', 'vendor-claim', 'user-voice']),
        sourceUrl: z.string().min(1).max(2000),
        sourceName: z.string().max(200).default(''),
        quote: z.string().max(1000).default(''),
        author: z.string().max(200).optional(),
        permalink: z.string().max(2000).optional(),
      }),
    )
    .default([]),
});
export type ChainDemandEntry = z.infer<typeof ChainDemandEntrySchema>;

export const MAX_PROJECTED_DEMAND = 30;

export const ChainProjectedEvidenceSchema = z.object({
  features: lenientList(ChainProjectedFeatureSchema, MAX_PROJECTED_FEATURES),
  /** Extracted claims the fact-check refused, so an emptied list reads differently from an empty one. */
  featuresDropped: z.number().int().nonnegative().default(0),
  opinions: lenientList(ChainOpinionSchema, MAX_PROJECTED_OPINIONS),
  /** Demand read off the same pages (ADR 0128). */
  demand: lenientList(ChainDemandEntrySchema, MAX_PROJECTED_DEMAND),
  /**
   * Why entries were refused, one per refusal — and the discriminator that lets the screen tell
   * "never asked" (empty) from "asked and the pages carried nothing" (`pages-never-said`).
   * A plain string list rather than the wire enum, for the same reason `marketRefused` is on the
   * read side: a member added by a newer producer must not fail the whole board's parse.
   */
  demandRefusals: z.array(z.string().max(60)).max(60).default([]),
});
export type ChainProjectedEvidence = z.infer<typeof ChainProjectedEvidenceSchema>;

// ── Supply & demand op log ───────────────────────────────────────────────────

export const SdOpTypeSchema = z.enum(['addSignal', 'addFeature', 'linkSF', 'unlinkSF', 'linkFP', 'unlinkFP', 'undo', 'redo']);
export type SdOpType = z.infer<typeof SdOpTypeSchema>;

/**
 * One entry in the append-only supply & demand op log. Undo/redo are ops too
 * (targeting an earlier op id), so the log is the full audit trail and the graph
 * is a pure replay of it.
 */
export const SdOpSchema = z.object({
  id: z.string().min(1),
  /** ISO timestamp from the client's injected clock (display + ordering hint only). */
  at: z.string(),
  type: SdOpTypeSchema,
  payload: z.record(z.string(), z.string()),
  label: z.string(),
});
export type SdOp = z.infer<typeof SdOpSchema>;

// ── Idea Engine handoff ──────────────────────────────────────────────────────

export const EngineQueueStatusSchema = z.enum(['ready', 'sent']);
export type EngineQueueStatus = z.infer<typeof EngineQueueStatusSchema>;

// ── Taxonomy: industries and the categories filed under them (ADR 0107) ────────

/**
 * A industry id. The ten built-ins are seeded in code (`industries.ts`) and keep their stable
 * keys (`financials`, `health-care`, …); an industry the analyst creates is minted one. Constrained to
 * the same shape as a seed key so an id is safe as a record key and a DOM value.
 */
export const IndustryIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'an industry id is lowercase letters, digits and hyphens');
export type IndustryId = z.infer<typeof IndustryIdSchema>;

/**
 * A *difference* from the seeded taxonomy, never the whole industry (ADR 0107). A workspace
 * that has not edited its taxonomy stores none of these and resolves entirely from the seed,
 * which is why nothing had to be back-filled when this landed.
 */
export const IndustryOverrideSchema = z.object({
  id: IndustryIdSchema,
  /** Renamed label. Null keeps the seeded label — and is invalid for an industry with no seed. */
  label: z.string().min(1).max(80).nullable(),
  /** Explicit position. Null keeps seed order; created industries sort after the seeds. */
  order: z.number().int().min(0).max(9999).nullable(),
  /**
   * A built-in the analyst removed. The seed lives in code and cannot be deleted by a row, so
   * removal is a tombstone the resolver skips — and a revert lifts it, restoring the seed intact.
   */
  removed: z.boolean(),
});
export type IndustryOverride = z.infer<typeof IndustryOverrideSchema>;

/**
 * One edit inside a `taxonomy.batch`. Two of them carry a destination rather than assuming one:
 * removing an industry or a category that still has members is refused without somewhere for those
 * members to go, so nothing is ever silently orphaned or silently re-derived.
 */
const CategoryNameSchema = z.string().min(1).max(120);

/**
 * Product ids (or category names) an edit re-files explicitly.
 *
 * A category is only *sometimes* a stored fact: a product with no manual mapping is filed by
 * a 38-rule keyword table over its industry text. So "move this category's products" cannot
 * be expressed as a `WHERE category = …` — half the members have no row saying so. Rather
 * than teach the database those rules (a second copy, free to drift from the first), the
 * client resolves the members and the edit names them. The op stays intent *and* says
 * precisely who it moves, and the server applies it without needing to re-derive anything.
 */
const RemapSchema = z.array(z.string().min(1)).max(5000).default([]);

export const TaxonomyEditSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('industry.create'), id: IndustryIdSchema, label: z.string().min(1).max(80) }),
  z.object({ op: z.literal('industry.rename'), id: IndustryIdSchema, label: z.string().min(1).max(80) }),
  z.object({
    op: z.literal('industry.remove'),
    id: IndustryIdSchema,
    moveCategoriesTo: IndustryIdSchema.nullable(),
    /** Category names to pin to the destination — including those the rules put here. */
    pin: RemapSchema,
  }),
  /** The full industry order, front to back. Ids absent from the list keep their relative order behind it. */
  z.object({ op: z.literal('industry.order'), ids: z.array(IndustryIdSchema).max(200) }),
  z.object({
    op: z.literal('category.create'),
    name: CategoryNameSchema,
    industry: IndustryIdSchema,
    /**
     * Product ids to file into the new category immediately — the products page's "create one
     * and put this product in it", which stays ONE ledger row rather than a create then a map.
     */
    remap: RemapSchema,
  }),
  z.object({
    op: z.literal('category.rename'),
    from: CategoryNameSchema,
    to: CategoryNameSchema,
    /** Product ids to point at the new name, so a rule-derived rename actually sticks. */
    remap: RemapSchema,
  }),
  z.object({
    op: z.literal('category.remove'),
    name: CategoryNameSchema,
    moveProductsTo: CategoryNameSchema.nullable(),
    /** Product ids to move to the destination. */
    remap: RemapSchema,
  }),
  z.object({ op: z.literal('category.industry'), name: CategoryNameSchema, industry: IndustryIdSchema }),
]);
export type TaxonomyEdit = z.infer<typeof TaxonomyEditSchema>;

/**
 * An edit as *authored* — the re-file lists are optional here because the resolver fills them
 * in. The editor expresses intent ("remove this, send its products there"); what goes on the
 * wire is a `TaxonomyEdit`, with the members named.
 */
export type TaxonomyEditInput = z.input<typeof TaxonomyEditSchema>;

// ── Analyst overlay ──────────────────────────────────────────────────────────

/**
 * Everything the analyst has decided on top of the researched dataset — the whole
 * mutable state of the app, keyed by product/category. Persisting exactly this shape
 * makes the client's optimistic ops replayable and the dataset reload idempotent.
 */
export const ChainOverlaySchema = z.object({
  /** Manual products appended outside any parsed round. */
  manualProducts: z.array(ChainProductSchema),
  /** Verdict overrides by product id (absent → the researcher's initial signal). */
  verdicts: z.record(z.string(), ChainVerdictSchema),
  /** "What more data is needed?" notes per product. */
  needNotes: z.record(z.string(), z.string()),
  /** Research-loop reviewed flags per product. */
  reviewed: z.record(z.string(), z.boolean()),
  /** Manual category mapping per product id (absent → canonical mapping). */
  categoryMap: z.record(z.string(), z.string()),
  /** Analyst-created categories beyond the canonical set. */
  customCategories: z.array(z.string()),
  /** Human classification per category (absent → engine suggestion). */
  categoryClass: z.record(z.string(), ChainClassificationSchema),
  /** Analyst notes on category drill panels, keyed `category:panel`. */
  categoryNotes: z.record(z.string(), z.string()),
  /** Evidence-field overrides keyed `productId:field` (field ∈ A–F). */
  evidence: z.record(z.string(), ChainEvidencePatchSchema),
  /** Idea-engine queue: productId → ready | sent. */
  engineQueue: z.record(z.string(), EngineQueueStatusSchema),
  /** Needs↔features rows per product (absent → seeded rows). */
  features: z.record(z.string(), z.array(ChainFeatureRowSchema)),
  /** Similar-product rows per product (absent → seeded rows). */
  similar: z.record(z.string(), z.array(ChainSimilarRowSchema)),
  /** Supply & demand op log (append-only). */
  sdHistory: z.array(SdOpSchema),
  /** Differences from the seeded industries — created, relabelled, reordered, tombstoned (ADR 0107). */
  industries: z.array(IndustryOverrideSchema),
  /** Which industry a category is filed under. Absent → the seeded GICS mapping still answers. */
  categoryIndustries: z.record(z.string(), IndustryIdSchema),
  /**
   * What agent runs established per product — cited features and public opinion (ECONOMY-21).
   *
   * In the overlay payload because it travels on the same load, but it is NOT analyst state: the
   * projector writes it and the analyst's own `features` rows win over it wherever both exist.
   */
  projected: z.record(z.string(), ChainProjectedEvidenceSchema).default({}),
});
export type ChainOverlay = z.infer<typeof ChainOverlaySchema>;

/** An empty overlay — the state of a fresh workspace before any analyst decision. */
export const EMPTY_CHAIN_OVERLAY: ChainOverlay = {
  manualProducts: [],
  verdicts: {},
  needNotes: {},
  reviewed: {},
  categoryMap: {},
  customCategories: [],
  categoryClass: {},
  categoryNotes: {},
  evidence: {},
  engineQueue: {},
  features: {},
  similar: {},
  sdHistory: [],
  industries: [],
  categoryIndustries: {},
  projected: {},
};

// ── Provenance & the op ledger (ADR 0095) ────────────────────────────────────

/**
 * Who/what produced a change — the provenance axis every datum carries.
 *
 * `intake` (ADR 0098) is a document the analyst uploaded, read by a model. Neither
 * neighbour is honest for it: `analyst` means *a human typed this*, which is the
 * distinction the whole manual-override-vs-researched axis rests on, and `sweep` means
 * *a worker went looking on the open web*. An `intake` row's `sourceRef` is
 * `doc:<documentId>`, which resolves to the stored file.
 */
export const OpOriginSchema = z.enum(['analyst', 'sweep', 'seed', 'system', 'intake']);
export type OpOrigin = z.infer<typeof OpOriginSchema>;

// ── Domain mutations (the op seam) ───────────────────────────────────────────

/**
 * Every domain mutation the UI can make, as one discriminated union — the client
 * applies each optimistically and the server acknowledges (or rejects → rollback).
 * Mirrors `ChainGateway.apply` in the web app 1:1; each kind maps to one persistence
 * write on the server.
 */
/**
 * The product fields an analyst may correct after commit (ADR 0106). Declared as a list so the
 * schema, the SQL patch and any UI that renders an editor cannot drift apart silently.
 */
export const PRODUCT_PATCHABLE_FIELDS = [
  'company', 'product', 'subcategory', 'does', 'link', 'regions', 'note', 'reason', 'obj',
] as const;

export type ProductPatchableField = (typeof PRODUCT_PATCHABLE_FIELDS)[number];

/**
 * A partial product. `.strict()` so an unknown or forbidden key is a REFUSAL naming the field
 * rather than a silently-ignored write — an edit that reports success and changed nothing is
 * the worst of the three possible outcomes.
 */
export const ProductPatchSchema = ChainProductSchema
  .pick(Object.fromEntries(PRODUCT_PATCHABLE_FIELDS.map((f) => [f, true])) as Record<ProductPatchableField, true>)
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'a patch must change at least one field' });

export type ProductPatch = z.infer<typeof ProductPatchSchema>;

export const ChainOpSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('verdict.set'), productId: z.string().min(1), verdict: ChainVerdictSchema }),
  z.object({ kind: z.literal('product.add'), product: ChainProductSchema }),
  z.object({ kind: z.literal('note.need.set'), productId: z.string().min(1), note: z.string() }),
  z.object({ kind: z.literal('product.reviewed.set'), productId: z.string().min(1), reviewed: z.boolean() }),
  z.object({ kind: z.literal('category.map'), productId: z.string().min(1), category: z.string().min(1) }),
  z.object({
    kind: z.literal('category.create'),
    name: z.string().min(1),
    /** The product the new category is immediately mapped to (the app's create flow). */
    productId: z.string().min(1).optional(),
  }),
  z.object({ kind: z.literal('category.classify'), category: z.string().min(1), cls: ChainClassificationSchema.nullable() }),
  z.object({ kind: z.literal('category.note.set'), key: z.string().min(1), note: z.string() }),
  /**
   * Rename an uploaded document (ADR 0109). ONE op rewriting all FOUR places the name has been
   * denormalised to: the document's `filename`, the round's `label`, the round's `doc`, and the
   * `round` stamped on every product that round produced. `prev` holds all four, so one revert
   * restores all four. (`intake_rounds.doc` was the one the first implementation missed — see
   * the migration `20260803160000_document_rename_round_doc.sql` and ADR 0109's 2026-08-03
   * correction.)
   *
   * It never touches `storage_path` or `original_filename` — the bytes stay where they are, under
   * the name they arrived with.
   */
  z.object({
    kind: z.literal('document.rename'),
    documentId: z.string().min(1),
    filename: z.string().min(1).max(240),
  }),
  /**
   * A screenful of taxonomy edits as ONE op (ADR 0107). Applied in order inside the single
   * transaction `apply_chain_op` already runs in, so a Save is atomic in the database and
   * atomic in the story the ledger tells: one row, one history entry, one revert. N separate
   * ops would instead leave a half-applied screen when the fourth of seven fails.
   */
  z.object({ kind: z.literal('taxonomy.batch'), edits: z.array(TaxonomyEditSchema).min(1).max(200) }),
  z.object({ kind: z.literal('evidence.set'), productId: z.string().min(1), field: z.string().min(1), patch: ChainEvidencePatchSchema }),
  z.object({ kind: z.literal('feature.rows.set'), productId: z.string().min(1), rows: z.array(ChainFeatureRowSchema) }),
  z.object({ kind: z.literal('similar.rows.set'), productId: z.string().min(1), rows: z.array(ChainSimilarRowSchema) }),
  z.object({ kind: z.literal('engine.queue.set'), productId: z.string().min(1), status: EngineQueueStatusSchema.nullable() }),
  z.object({ kind: z.literal('sd.op.append'), op: SdOpSchema }),
  z.object({ kind: z.literal('demo.reset') }),
  /**
   * HARD deletes (ADR 0100 §5). Each removes rows outright — the analyst's confirmation
   * dialogue is the safety, not an undo. They are non-revertible barriers like
   * `demo.reset`: `prev` is captured so history can say *what* went, but a revert will
   * not resurrect it, because an undo that quietly restored the rows would make the
   * confirmation a lie.
   */
  /**
   * Correct a committed product, field by field (ADR 0106).
   *
   * A PATCH, not a replace, and that matters three ways: `prev` holds only what was actually
   * overwritten so a revert cannot resurrect unrelated stale values; two people editing
   * different fields of one product do not clobber each other; and ECONOMY-13's "apply only
   * the ticked fields" is this op with a different caller rather than a second write path.
   *
   * The accepted keys are an ALLOW-LIST. Identity and provenance (`id`, `base`, `intakeId`,
   * `origin`, `sourceRef`, `opSeq`, `manual`) are structurally unreachable — a patch may
   * change what a product *says*, never what it *is* or where it came from. `signal` is
   * excluded too: `verdict.set` owns the verdict, and two ops able to change one thing is how
   * a ledger stops being a single story.
   */
  z.object({
    kind: z.literal('product.update'),
    productId: z.string().min(1),
    fields: ProductPatchSchema,
  }),
  z.object({ kind: z.literal('product.discard'), productId: z.string().min(1) }),
  /** The round's committed products and its uploaded documents, gone. */
  z.object({ kind: z.literal('intake.round.discard'), roundId: z.string().min(1) }),
  /** Everything this owner has — products, overlay, rounds, documents, staging, sweeps, ledger. */
  z.object({ kind: z.literal('workspace.wipe') }),
  /** Roll back an earlier ledger op (ADR 0095). Refused for demo.reset / sd.op.append / already-reverted targets. */
  z.object({ kind: z.literal('op.revert'), targetOpId: z.string().min(1) }),
]);
export type ChainOp = z.infer<typeof ChainOpSchema>;

/**
 * One row of the append-only op ledger, as served by the history endpoint: the op, the
 * exact state it replaced (`prev`, captured server-side in the same transaction), its
 * provenance, and whether a later revert disabled it.
 */
export const ChainOpRecordSchema = z.object({
  /** Server total order. */
  seq: z.number().int().positive(),
  /** Client ULID (idempotency key). */
  opId: z.string().min(1),
  kind: z.string().min(1),
  payload: z.record(z.string(), z.unknown()),
  /** The state this op replaced (null = the op created its row / non-revertible barrier). */
  prev: z.record(z.string(), z.unknown()).nullable(),
  origin: OpOriginSchema,
  /** E.g. a sweep id or document ref backing a researched write. */
  sourceRef: z.string().nullable(),
  /** The UI command id that caused the op (threads the audit trail end-to-end). */
  correlationId: z.string().nullable(),
  occurredAt: z.string(),
  /** Seq of the revert op that undid this one (null = active). */
  revertedBy: z.number().int().positive().nullable(),
  /**
   * WHO made the change — the resolved principal (ADR 0106). Null for anything written
   * before identity existed, or by a worker with no person behind it. Deliberately not
   * defaulted to a string: "we do not know" and "someone" are different claims.
   */
  actor: z.string().nullable().default(null),
});
export type ChainOpRecord = z.infer<typeof ChainOpRecordSchema>;

/**
 * Which product a ledger row is *about* — the key the per-product history strip filters on.
 *
 * Most kinds name their subject as a top-level `productId`, but `product.add` carries the
 * whole product (`payload.product.id`). Filtering on `productId` alone therefore hid the
 * single row that explains where a sweep-found product came from, so the knowledge of both
 * shapes lives here once, next to the op schemas, rather than in each store adapter. The
 * SQL side derives the same value into the indexed `chain_ops.product_id` column
 * (`20260727130000_chain_ops_product_scope.sql`) — keep the two in step.
 */
export function chainOpProductId(payload: Readonly<Record<string, unknown>>): string | null {
  const direct = payload['productId'];
  if (typeof direct === 'string' && direct !== '') return direct;
  const product = payload['product'];
  if (product !== null && typeof product === 'object') {
    const id = (product as Record<string, unknown>)['id'];
    if (typeof id === 'string' && id !== '') return id;
  }
  return null;
}

// ── Background sweeps (worker shapes) ────────────────────────────────────────

/**
 * `document` (ADR 0098) extracts an uploaded research document rather than combing a
 * channel. It is a sweep kind because extraction rides the same queue: the analyst gets
 * retries, lease reclaim, progress, a readable log and cancellation for free, and a long
 * extraction can never sit inside a request the browser is waiting on.
 */
export const SweepKindSchema = z.enum(['objective', 'demand', 'category', 'alternatives', 'keyword', 'prompt', 'document']);
export type SweepKind = z.infer<typeof SweepKindSchema>;

/** Pipeline section a sweep feeds (drives the map badges). */
export const SweepAreaSchema = z.enum(['intake', 'sources', 'cats', 'enrich', 'demand']);
export type SweepArea = z.infer<typeof SweepAreaSchema>;

/**
 * The exact `Sweep.error` a cancelled sweep carries. The UI suppresses its failure toast
 * on this value and the roll-up keeps a cancelled sweep cancelled by comparing it, so it
 * is a wire contract shared by the app, the API and the queue SQL — not a display string.
 */
export const SWEEP_CANCELLED_ERROR = 'cancelled by analyst';

export const SweepStatusSchema = z.enum(['queued', 'running', 'done', 'failed']);
export type SweepStatus = z.infer<typeof SweepStatusSchema>;

/** Exactly what a sweep combs — all optional; kinds use the fields they need. */
export const SweepScopeSchema = z.record(z.string(), z.string());
export type SweepScope = z.infer<typeof SweepScopeSchema>;

export const SweepLogEntrySchema = z.object({
  /** Epoch ms. */
  at: z.number(),
  level: z.enum(['info', 'new', 'dup', 'error', 'done']),
  text: z.string(),
});
export type SweepLogEntry = z.infer<typeof SweepLogEntrySchema>;

export const SweepCountsSchema = z.object({
  new: z.number().int().nonnegative(),
  duplicates: z.number().int().nonnegative(),
  /** Baseline already in the DB for this scope before the sweep started — constant. */
  known: z.number().int().nonnegative(),
});
export type SweepCounts = z.infer<typeof SweepCountsSchema>;

/** A background AI research worker, as reported by the sweeps API. */
export const SweepSchema = z.object({
  id: z.string().min(1),
  kind: SweepKindSchema,
  area: SweepAreaSchema,
  label: z.string().min(1),
  scope: SweepScopeSchema,
  sources: z.array(z.string()),
  status: SweepStatusSchema,
  progress: z.number().min(0).max(100),
  /** Epoch ms. */
  startedAt: z.number(),
  counts: SweepCountsSchema,
  log: z.array(SweepLogEntrySchema),
  error: z.string().nullable(),
});
export type Sweep = z.infer<typeof SweepSchema>;
